export const PIC_DELIMITER = "|";

export type SideLoadedMatchedField = "abn" | "pic" | "brands" | "email";

export const MATCHED_FIELD_LABELS: Record<SideLoadedMatchedField, string> = {
  abn: "ABN",
  pic: "PIC",
  brands: "Brands",
  email: "Email",
};

// A row in either the Data Suggestions grid or the Manage grid. Both source
// their rows from selectSideLoadedBusinessesGridList, so datasetLabel is always
// present; matchedFields only applies to a searched suggestion.
export type SideLoadedBusinessRow = SideLoadedBusiness & {
  datasetLabel: string;
  matchedFields?: SideLoadedMatchedField[];
  validationIssues?: string[];
};

// Mirrors PIC_COLUMN_DELIMITERS on the API.
export const PIC_CELL_DELIMITERS = [",", ";", PIC_DELIMITER];

// Splits back out on the way in, so a hand edit can be typed the way it reads.
export const PIC_DISPLAY_DELIMITER = ", ";

// Uppercased, unlike label names - a PIC has a canonical form.
export const parsePicValues = (
  values: (string | undefined | null)[],
): string[] => {
  const seen = new Set<string>();
  return values.reduce<string[]>((acc, value) => {
    PIC_CELL_DELIMITERS.reduce<string[]>(
      (parts, delimiter) => parts.flatMap(part => part.split(delimiter)),
      [value ?? ""],
    ).forEach(raw => {
      const pic = raw.trim().toUpperCase();
      if (!pic || seen.has(pic)) return;
      seen.add(pic);
      acc.push(pic);
    });
    return acc;
  }, []);
};

export const LABEL_DELIMITER = ",";

/**
 * Split a labels cell into distinct names, mirroring the parsing the API does
 * on import so the compare form shows exactly what will be applied.
 */
export const parseLabelNames = (value: string | undefined | null): string[] => {
  const seen = new Set<string>();
  return (value ?? "").split(LABEL_DELIMITER).reduce<string[]>((acc, raw) => {
    const name = raw.trim();
    if (!name || seen.has(name.toLowerCase())) return acc;
    seen.add(name.toLowerCase());
    acc.push(name);
    return acc;
  }, []);
};

// Label names come from a customer's CSV, so they need trimming to sit in a
// toast, or against a row in the compare form.
export const quoteLabelName = (name: string): string =>
  name.length > 20 ? `"${name.slice(0, 20)}…"` : `"${name}"`;

// A label has to exist before a side-loaded record can apply it, so anything
// this returns is skipped on commit.
export const findUnknownLabelNames = (
  names: string[],
  knownLabelNames: string[],
): string[] => {
  const known = new Set(knownLabelNames.map(name => name.toLowerCase()));
  return names.filter(name => !known.has(name.toLowerCase()));
};

const formatLabelNameList = (names: string[]): string => {
  const quoted = names.map(quoteLabelName);
  return quoted.length > 1
    ? `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}`
    : quoted[0];
};

// Deliberately not a validation error - a missing label shouldn't stop the
// rest of the record being used.
export const formatUnknownLabelsWarning = (names: string[]): string | null => {
  if (!names.length) {
    return null;
  }
  return `${formatLabelNameList(names)} ${
    names.length > 1 ? "are" : "is"
  } not set up yet and will be skipped.`;
};

// The same event once it has happened, so it shares the list formatting with
// the row's warning and differs only in tense.
export const formatSkippedLabelsMessage = (names: string[]): string | null => {
  if (!names.length) {
    return null;
  }
  return `Skipped ${formatLabelNameList(names)} - not set up yet.`;
};
